import React from 'react';

interface CategorySkeletonProps {
  title: string;
  itemCount?: number;
}

export function CategorySkeleton({ title, itemCount = 3 }: CategorySkeletonProps) {
  console.log(`[CategorySkeleton] Rendering skeleton for ${title}`);
  
  return (
    <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
      {/* Category Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-5 h-5 bg-gray-700 rounded animate-pulse" />
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        <div className="w-16 h-6 bg-gray-700 rounded animate-pulse" />
      </div>

      {/* Score Section */}
      <div className="mb-6">
        <div className="w-24 h-4 bg-gray-700 rounded animate-pulse mb-2" />
        <div className="w-full bg-gray-800 rounded-full h-2">
          <div className="w-3/4 h-2 bg-gray-600 rounded-full animate-pulse" />
        </div>
      </div>

      {/* Finding Items */}
      <div className="space-y-3">
        {Array.from({ length: itemCount }).map((_, index) => (
          <div key={index} className="border border-gray-800 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="w-3/4 h-4 bg-gray-700 rounded animate-pulse mb-2" />
                <div className="w-1/2 h-3 bg-gray-800 rounded animate-pulse" />
              </div>
              <div className="w-16 h-6 bg-gray-700 rounded-full animate-pulse ml-4" />
            </div>
            <div className="mt-3">
              <div className="w-full h-3 bg-gray-800 rounded animate-pulse mb-1" />
              <div className="w-2/3 h-3 bg-gray-800 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface ScanSkeletonProps {
  showProgress?: boolean;
}

export function ScanSkeleton({ showProgress = false }: ScanSkeletonProps) {
  console.log('[ScanSkeleton] Rendering full scan skeleton');
  
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header Skeleton */}
      <div className="text-center">
        <div className="w-64 h-8 bg-gray-700 rounded animate-pulse mx-auto mb-4" />
        <div className="w-96 h-4 bg-gray-800 rounded animate-pulse mx-auto" />
      </div>

      {/* Progress Bar (if shown) */}
      {showProgress && (
        <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
          <div className="w-32 h-4 bg-gray-700 rounded animate-pulse mb-4" />
          <div className="w-full bg-gray-800 rounded-full h-3">
            <div className="w-1/3 h-3 bg-blue-600 rounded-full animate-pulse" />
          </div>
          <div className="w-48 h-3 bg-gray-800 rounded animate-pulse mt-2" />
        </div>
      )}

      {/* Summary Stats Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {['Security', 'Privacy', 'Compliance', 'Overall'].map((category) => (
          <div key={category} className="bg-gray-900 rounded-lg p-6 border border-gray-800">
            <div className="w-20 h-4 bg-gray-700 rounded animate-pulse mb-2" />
            <div className="w-12 h-8 bg-gray-700 rounded animate-pulse mb-3" />
            <div className="w-full bg-gray-800 rounded-full h-2">
              <div className="w-2/3 h-2 bg-gray-600 rounded-full animate-pulse" />
            </div>
          </div>
        ))}
      </div>

      {/* Category Sections Skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <CategorySkeleton title="Security Issues" itemCount={2} />
        <CategorySkeleton title="Privacy Issues" itemCount={1} />
        <CategorySkeleton title="Compliance Issues" itemCount={2} />
        <CategorySkeleton title="Other Issues" itemCount={1} />
      </div>
    </div>
  );
} 